import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Shield, ArrowRight, Loader2, CreditCard, Truck, ExternalLink, AlertCircle } from 'lucide-react';

const PLATFORMS = [
  {
    id: 'sellesend',
    name: 'SelleSend',
    description: 'Shipping & Protection Platform',
    url: null,
    icon: Shield,
    color: 'from-emerald-500 to-blue-500',
    ring: 'ring-emerald-500',
    active: true
  },
  {
    id: 'sellecheck',
    name: 'SelleCheck',
    description: 'Verification & Compliance',
    url: 'https://sellecheck.com',
    icon: Shield,
    color: 'from-blue-600 to-indigo-600',
    ring: 'ring-blue-500',
    active: false
  },
  {
    id: 'sellecheck-gateway',
    name: 'SelleCheck Gateway',
    description: 'Transaction Payment Gateway',
    url: 'https://sellecheck.transactiongateway.com',
    icon: CreditCard,
    color: 'from-purple-600 to-pink-600',
    ring: 'ring-purple-500',
    active: false
  },
  {
    id: 'sellepack',
    name: 'SelleGard Freight',
    description: 'Discounted labels & rebill',
    url: '/dashboard/sellegard',
    icon: Truck,
    color: 'from-amber-500 to-orange-600',
    ring: 'ring-amber-500',
    active: true
  }
];

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('sellesend');
  // El aviso de sonner sale en la esquina superior derecha, sobre la foto
  // oscura, al otro lado de la pantalla y se borra solo. Nadie lo ve. El
  // error de entrada va debajo del campo y se queda.
  const [errorEntrada, setErrorEntrada] = useState('');
  const [mfa, setMfa] = useState(null); // { challenge_token, methods, sms_phone_last4 }
  const [mfaCode, setMfaCode] = useState('');
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setErrorEntrada('');

    const platform = PLATFORMS.find(p => p.id === selectedPlatform);
    if (platform && platform.url) {
      // Si el navegador bloquea la ventana, window.open devuelve null. Antes
      // se salia igual: ni abria, ni entraba, ni decia nada.
      const abierta = window.open(platform.url, '_blank');
      if (!abierta) {
        setErrorEntrada(
          `Your browser blocked the window for ${platform.name}. Allow pop-ups for this site, ` +
          `or open ${platform.url} yourself. Nothing was signed in here.`);
      }
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.mfa_required) {
        setMfa({
          challenge_token: result.challenge_token,
          methods: result.methods || [],
          sms_phone_last4: result.sms_phone_last4,
        });
        toast.info('Two-factor code required');
        return;
      }
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const detalle = error.response?.data?.detail;
      const message = typeof detalle === 'string'
        ? detalle
        : (error.response ? 'Invalid credentials' : 'Could not reach the server. Check your connection and try again.');
      setErrorEntrada(message);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setErrorEntrada('');
    setLoading(true);
    try {
      await verifyMfa(mfa.challenge_token, mfaCode);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const detalle = error.response?.data?.detail;
      const message = typeof detalle === 'string'
        ? detalle
        : (error.response ? 'Invalid or expired code' : 'Could not reach the server. Check your connection and try again.');
      setErrorEntrada(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlatformSelect = (platformId) => {
    const platform = PLATFORMS.find(p => p.id === platformId);
    setSelectedPlatform(platformId);
    if (platform && platform.url) {
      window.open(platform.url, '_blank');
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left - Form */}
      <div className="flex flex-col justify-center px-8 py-12 lg:px-16">
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="logo-link">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-semibold tracking-tight">SelleSend</span>
        </Link>

        {/* Platform Radials */}
        <div className="mb-8" data-testid="platform-selector">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">SELECT PLATFORM</p>
          <div className="grid grid-cols-2 gap-2">
            {PLATFORMS.map((p) => {
              const Icon = p.icon;
              const isSelected = selectedPlatform === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handlePlatformSelect(p.id)}
                  className={`relative text-left p-3 rounded-xl border-2 transition-all ${
                    isSelected
                      ? `border-transparent ring-2 ${p.ring} bg-slate-50 dark:bg-slate-800 shadow-sm`
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                  data-testid={`platform-${p.id}`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${p.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{p.name}</p>
                      <p className="text-[10px] text-slate-400 truncate">{p.description}</p>
                    </div>
                  </div>
                  {p.url && (
                    <ExternalLink className="absolute top-2 right-2 w-3 h-3 text-slate-300" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="max-w-md animate-fade-in">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2">
            Welcome back
          </h1>
          <p className="text-slate-500 mb-8">
            {selectedPlatform === 'sellesend'
              ? 'Sign in to access your shipping dashboard'
              : `Redirecting to ${PLATFORMS.find(p => p.id === selectedPlatform)?.name}...`
            }
          </p>

          <form onSubmit={mfa ? handleMfaSubmit : handleSubmit} className="space-y-6">
            {mfa ? (
              <div className="space-y-4" data-testid="mfa-challenge">
                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-sm">
                  Enter the 6-digit code from your authenticator app
                  {mfa.methods.includes('sms') && mfa.sms_phone_last4 && ` or the SMS we sent to ····${mfa.sms_phone_last4}`}
                  . Recovery codes work too.
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mfa-code">Verification code</Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    inputMode="text"
                    autoComplete="one-time-code"
                    placeholder="123 456"
                    value={mfaCode}
                    onChange={(e) => { setMfaCode(e.target.value); setErrorEntrada(''); }}
                    required
                    autoFocus
                    className="h-12 tracking-widest text-center text-lg font-mono"
                    data-testid="mfa-code-input"
                  />
                </div>
                <button type="button" onClick={() => { setMfa(null); setMfaCode(''); }} className="text-xs text-slate-500 hover:underline" data-testid="mfa-cancel">
                  ← Back to sign-in
                </button>
              </div>
            ) : (
              <>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrorEntrada(''); }}
                required
                className="h-12"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-sm text-secondary hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setErrorEntrada(''); }}
                required
                className="h-12"
                data-testid="login-password-input"
              />
            </div>
              </>
            )}

            {errorEntrada && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2.5 text-sm text-rose-700"
                data-testid="login-error">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{errorEntrada}</span>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full h-12 btn-press"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {mfa ? 'Verify & continue' : 'Sign In'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-slate-500 mt-8">
            Don't have an account?{' '}
            <Link to="/register" className="text-secondary font-medium hover:underline" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Right - Image */}
      <div className="hidden lg:block relative">
        <img 
          src="https://images.pexels.com/photos/27111449/pexels-photo-27111449.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Warehouse"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="text-2xl font-medium mb-4">
            Every shipment gets a fingerprint, a timestamp and a coverage record
            your buyer can check for themselves.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
